import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { FiHome } from 'react-icons/fi';
import { Drawer, Button, FormSection, Input, Select } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { api } from '@/lib/api';
import type { ApiResponse } from '@/lib/api';
import type { CompanyResponse, UpdateCompanyRequest } from '@troia-v3/types';
import { CompanyCustomizationTab } from './CompanyCustomizationTab';
import { CompanyBusinessHoursTab } from './CompanyBusinessHoursTab';

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

const COUNTRY_OPTIONS = [
  { value: 'BR', label: 'Brasil (+55)' },
  { value: 'US', label: 'Estados Unidos (+1)' },
  { value: 'PT', label: 'Portugal (+351)' },
  { value: 'AR', label: 'Argentina (+54)' },
  { value: 'MX', label: 'México (+52)' },
  { value: 'CO', label: 'Colômbia (+57)' },
  { value: 'CL', label: 'Chile (+56)' },
  { value: 'UY', label: 'Uruguai (+598)' },
  { value: 'PY', label: 'Paraguai (+595)' },
  { value: 'PE', label: 'Peru (+51)' },
  { value: 'DE', label: 'Alemanha (+49)' },
  { value: 'ES', label: 'Espanha (+34)' },
  { value: 'FR', label: 'França (+33)' },
  { value: 'GB', label: 'Reino Unido (+44)' },
  { value: 'IT', label: 'Itália (+39)' },
  { value: 'JP', label: 'Japão (+81)' },
];

const TIMEZONE_OPTIONS = [
  { value: 'America/Sao_Paulo', label: 'Brasília (GMT-3) — São Paulo' },
  { value: 'America/Manaus', label: 'Amazonas (GMT-4) — Manaus' },
  { value: 'America/Rio_Branco', label: 'Acre (GMT-5) — Rio Branco' },
  { value: 'America/Fortaleza', label: 'Nordeste (GMT-3) — Fortaleza' },
  { value: 'America/Noronha', label: 'Fernando de Noronha (GMT-2)' },
  { value: 'America/Cuiaba', label: 'Mato Grosso (GMT-4) — Cuiabá' },
  { value: 'America/Belem', label: 'Pará (GMT-3) — Belém' },
];

type TabId = 'empresa' | 'personalizacao' | 'horarios';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

interface CompanyEditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Tab to open by default */
  defaultTab?: TabId;
}

interface CompanyFormData {
  name: string;
  email: string;
  phone: string;
  website: string;
  logo: string;
  document: string;
  documentType: 'CPF' | 'CNPJ';
  defaultCountryCode: string;
  timezone: string;
  address: {
    street: string;
    number: string;
    complement: string;
    neighborhood: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

const INITIAL_FORM_DATA: CompanyFormData = {
  name: '',
  email: '',
  phone: '',
  website: '',
  logo: '',
  document: '',
  documentType: 'CNPJ',
  defaultCountryCode: 'BR',
  timezone: 'America/Sao_Paulo',
  address: {
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Brasil',
  },
};

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export const CompanyEditDrawer: React.FC<CompanyEditDrawerProps> = ({
  isOpen,
  onClose,
  defaultTab = 'empresa',
}) => {
  const [activeTab, setActiveTab] = useState<TabId>(defaultTab);
  const [formData, setFormData] = useState<CompanyFormData>(INITIAL_FORM_DATA);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Reset tab when drawer opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);

  // ─── Fetch company data ───────────────────────────────
  const fetchCompany = useCallback(async () => {
    if (!isOpen) return;
    setFetching(true);
    try {
      const response = await api.get<ApiResponse<CompanyResponse>>('companies/me');
      const company = response.data.data;
      if (company) {
        setFormData({
          name: company.name || '',
          email: company.email || '',
          phone: company.phone || '',
          website: company.website || '',
          logo: company.logo || '',
          document: company.document || '',
          documentType: company.documentType || 'CNPJ',
          defaultCountryCode: company.defaultCountryCode || 'BR',
          timezone: company.timezone || 'America/Sao_Paulo',
          address: {
            street: company.address?.street || '',
            number: company.address?.number || '',
            complement: company.address?.complement || '',
            neighborhood: company.address?.neighborhood || '',
            city: company.address?.city || '',
            state: company.address?.state || '',
            zipCode: company.address?.zipCode || '',
            country: company.address?.country || 'Brasil',
          },
        });
      }
    } catch {
      toast.error('Erro ao carregar dados da empresa');
    } finally {
      setFetching(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchCompany();
    }
  }, [isOpen, fetchCompany]);

  // ─── Field handlers ───────────────────────────────────
  const handleFieldChange = (field: keyof CompanyFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field: keyof CompanyFormData['address'], value: string) => {
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  // ─── Logo Upload ──────────────────────────────────────
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem válida');
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('uploads?folder=settings', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = response.data.data.url as string;
      setFormData((prev) => ({ ...prev, logo: url }));
      toast.success('Logo enviada com sucesso');
    } catch {
      toast.error('Erro ao enviar logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  // ─── Save ─────────────────────────────────────────────
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome da empresa é obrigatório');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Email da empresa é obrigatório');
      return;
    }

    setLoading(true);
    try {
      const payload: UpdateCompanyRequest = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        website: formData.website.trim() || undefined,
        logo: formData.logo.trim() || undefined,
        document: formData.document.trim() || undefined,
        documentType: formData.document.trim() ? formData.documentType : undefined,
        defaultCountryCode: formData.defaultCountryCode || undefined,
        timezone: formData.timezone || undefined,
        address: {
          street: formData.address.street.trim(),
          number: formData.address.number.trim(),
          complement: formData.address.complement.trim() || undefined,
          neighborhood: formData.address.neighborhood.trim(),
          city: formData.address.city.trim(),
          state: formData.address.state.trim(),
          zipCode: formData.address.zipCode.trim(),
          country: formData.address.country.trim(),
        },
      };

      await api.put('companies/me', payload);
      toast.success('Empresa atualizada com sucesso');
      onClose();
    } catch {
      toast.error('Erro ao atualizar empresa');
    } finally {
      setLoading(false);
    }
  };

  // ─── Footer (only for Empresa tab) ────────────────────
  const footer = activeTab === 'empresa' ? (
    <>
      <Button variant="secondary" onClick={onClose} disabled={loading}>
        Cancelar
      </Button>
      <Button variant="primary" onClick={handleSave} loading={loading}>
        Salvar
      </Button>
    </>
  ) : undefined;

  // ─── Render ───────────────────────────────────────────
  // F76: header padrão do Drawer + prop `tabs` (customHeader aposentado)
  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      icon={<FiHome className="w-5 h-5" />}
      title="Empresa"
      subtitle="Gerencie os dados e a aparência da empresa"
      tabs={{
        items: [
          { id: 'empresa', label: 'Dados da Empresa' },
          { id: 'personalizacao', label: 'Personalização' },
          { id: 'horarios', label: 'Horário de atendimento' },
        ],
        active: activeTab,
        onChange: (id) => setActiveTab(id as TabId),
      }}
      disabled={loading}
      footer={footer}
    >
      <div className="px-6 py-5">
      {activeTab === 'empresa' && (
        <>
          {fetching ? (
            <div className="flex items-center justify-center py-12">
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2"
                style={{ borderColor: '#8b5cf6' }}
              />
            </div>
          ) : (
            <div className="space-y-4">
              {/* ── Logo (F85: FormSection shared) ────────────── */}
              <FormSection icon="image" title="Logo da Empresa">
              <div className="flex items-center gap-4">
                <div
                  className="w-16 h-16 rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer"
                  style={{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }}
                  onClick={() => logoInputRef.current?.click()}
                >
                  {formData.logo ? (
                    <img src={formData.logo} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Icon name="image" className="w-6 h-6" style={{ color: '#94a3b8' }} />
                  )}
                </div>
                <div className="flex-1">
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors"
                    style={{ borderColor: '#e2e8f0', color: '#1e293b' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    {uploadingLogo ? 'Enviando...' : formData.logo ? 'Alterar logo' : 'Enviar logo'}
                  </button>
                  {formData.logo && (
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, logo: '' }))}
                      className="ml-2 text-xs"
                      style={{ color: '#ef4444' }}
                    >
                      Remover
                    </button>
                  )}
                  <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
                    PNG, JPG ou SVG. Recomendado: 200x200px
                  </p>
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
              </div>
              </FormSection>

              {/* ── Dados Gerais ─────────────────────────────── */}
              <FormSection icon="file-text" title="Dados Gerais">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    Nome <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    placeholder="Nome da empresa"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    Email <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    placeholder="email@empresa.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    Telefone
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    Website
                  </label>
                  <Input
                    value={formData.website}
                    onChange={(e) => handleFieldChange('website', e.target.value)}
                    placeholder="https://empresa.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    Tipo de Documento
                  </label>
                  <Select
                    options={[
                      { value: 'CNPJ', label: 'CNPJ' },
                      { value: 'CPF', label: 'CPF' },
                    ]}
                    value={formData.documentType}
                    onChange={(value) => handleFieldChange('documentType', value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    {formData.documentType === 'CPF' ? 'CPF' : 'CNPJ'}
                  </label>
                  <Input
                    value={formData.document}
                    onChange={(e) => handleFieldChange('document', e.target.value)}
                    placeholder={formData.documentType === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'}
                  />
                </div>
              </div>
              </FormSection>

              {/* ── Configurações ────────────────────────────── */}
              <FormSection icon="settings" title="Configurações">
              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="País padrão (DDI)"
                  options={COUNTRY_OPTIONS}
                  value={formData.defaultCountryCode}
                  onChange={(value) => handleFieldChange('defaultCountryCode', value)}
                  placeholder="Selecione um país"
                />
                <Select
                  label="Fuso horário"
                  options={TIMEZONE_OPTIONS}
                  value={formData.timezone}
                  onChange={(value) => handleFieldChange('timezone', value)}
                  placeholder="Selecione o fuso"
                />
              </div>
              </FormSection>

              {/* ── Endereço ─────────────────────────────────── */}
              <FormSection icon="map-pin" title="Endereço">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    Rua
                  </label>
                  <Input
                    value={formData.address.street}
                    onChange={(e) => handleAddressChange('street', e.target.value)}
                    placeholder="Nome da rua"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    Número
                  </label>
                  <Input
                    value={formData.address.number}
                    onChange={(e) => handleAddressChange('number', e.target.value)}
                    placeholder="Número"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    Complemento
                  </label>
                  <Input
                    value={formData.address.complement}
                    onChange={(e) => handleAddressChange('complement', e.target.value)}
                    placeholder="Apto, sala, etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    Bairro
                  </label>
                  <Input
                    value={formData.address.neighborhood}
                    onChange={(e) => handleAddressChange('neighborhood', e.target.value)}
                    placeholder="Bairro"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    Cidade
                  </label>
                  <Input
                    value={formData.address.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    placeholder="Cidade"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    Estado
                  </label>
                  <Input
                    value={formData.address.state}
                    onChange={(e) => handleAddressChange('state', e.target.value)}
                    placeholder="Estado"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    CEP
                  </label>
                  <Input
                    value={formData.address.zipCode}
                    onChange={(e) => handleAddressChange('zipCode', e.target.value)}
                    placeholder="00000-000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                    País
                  </label>
                  <Input
                    value={formData.address.country}
                    onChange={(e) => handleAddressChange('country', e.target.value)}
                    placeholder="País"
                  />
                </div>
              </div>
              </FormSection>
            </div>
          )}
        </>
      )}

      {activeTab === 'personalizacao' && (
        <CompanyCustomizationTab isOpen={isOpen} />
      )}

      {activeTab === 'horarios' && (
        <CompanyBusinessHoursTab isOpen={isOpen} />
      )}
      </div>
    </Drawer>
  );
};

CompanyEditDrawer.displayName = 'CompanyEditDrawer';
